/**
 * House Property Income working paper — AXIORA (Feb 2026)
 *
 * Route: /tax/house-property
 * One row per property. Engine auto-computes GAV → NAV → 30% deduction →
 * interest u/s 24(b), respecting SOP / LOP rules and regime restrictions
 * (Sec 115BAC(2) — SOP interest not allowed under New; LOP loss not
 * set-off vs other heads).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Send, X, Check, Home, Info, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PROPERTY_TYPES = [
  { value: "SOP", label: "Self-Occupied (SOP)" },
  { value: "LOP", label: "Let-Out (LOP)" },
  { value: "DLO", label: "Deemed Let-Out (DLO)" },
];
const TYPE_LABEL = Object.fromEntries(PROPERTY_TYPES.map((t) => [t.value, t.label]));

const currentAY = () => {
  const d = new Date();
  const fyStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${fyStart + 1}-${String((fyStart + 2) % 100).padStart(2, "0")}`;
};

const EMPTY_ROW = () => ({
  property_name: "",
  property_type: "SOP",
  address: "",
  rent_received: 0, municipal_value: 0, fair_rent: 0, standard_rent: 0,
  vacancy_period_months: 0, unrealised_rent: 0,
  municipal_taxes_paid: 0,
  interest_current_year: 0,
  pre_construction_interest_total: 0,
  pre_construction_years_remaining: 5,
  loan_taken_before_1_apr_1999: false,
  include_in_computation: true, notes: "",
});

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [ay, setAy] = useState(currentAY());
  const [regime, setRegime] = useState("New");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pushOpen, setPushOpen] = useState(false);
  const [computations, setComputations] = useState([]);
  const [selectedCompId, setSelectedCompId] = useState("");

  const refresh = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await api.get(`/companies/${activeId}/house-property/summary`, { params: { ay, regime } });
      setRows(r.data.rows || []);
      setSummary(r.data);
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [activeId, ay, regime]);

  const openNew  = () => { setEditing({ ...EMPTY_ROW() }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setDialogOpen(true); };
  const save = async () => {
    try {
      const payload = { ...editing, ay };
      if (editing.id) {
        await api.patch(`/companies/${activeId}/house-property/rows/${editing.id}`, payload);
      } else {
        await api.post(`/companies/${activeId}/house-property/rows`, payload);
      }
      toast.success("Saved");
      setDialogOpen(false);
      refresh();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.property_name || "property"}"?`)) return;
    try {
      await api.delete(`/companies/${activeId}/house-property/rows/${r.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };
  const openPushDialog = async () => {
    setPushOpen(true);
    try {
      const r = await api.get(`/companies/${activeId}/tax/computations`);
      setComputations(r.data || []);
      setSelectedCompId(r.data?.[0]?.id || "");
    } catch (err) { toast.error(describeAxiosError(err)); }
  };
  const pushToITR = async () => {
    if (!selectedCompId) return;
    try {
      const r = await api.post(
        `/companies/${activeId}/house-property/push-to-itr/${selectedCompId}`,
        null, { params: { ay } },
      );
      toast.success(`Pushed ₹${formatINR(r.data.summary.included_income)} (${r.data.regime_used} regime) → ITR`);
      setPushOpen(false);
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const totals = summary?.totals || { aggregate_income: 0, included_income: 0, total_rows: 0, included_rows: 0 };
  const perRow = summary?.per_row || [];
  const isLop = editing?.property_type === "LOP" || editing?.property_type === "DLO";

  return (
    <>
      <PageHeader
        title="House Property · Working Paper"
        subtitle="Sections 22-27. Handles SOP / Let-Out / Deemed Let-Out with regime-specific interest rules."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => nav("/tax")} className="h-8" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <select value={ay} onChange={(e) => setAy(e.target.value)} className="h-8 px-2 border border-gray-300 rounded text-xs" data-testid="select-ay">
              {["2024-25", "2025-26", "2026-27", "2027-28"].map((a) => <option key={a} value={a}>AY {a}</option>)}
            </select>
            <select value={regime} onChange={(e) => setRegime(e.target.value)} className="h-8 px-2 border border-gray-300 rounded text-xs" data-testid="select-regime">
              <option value="New">New Regime</option>
              <option value="Old">Old Regime</option>
            </select>
            <Button onClick={openPushDialog} variant="secondary" className="h-8" disabled={totals.total_rows === 0} data-testid="btn-push-itr">
              <Send className="w-4 h-4 mr-1" /> Push to ITR
            </Button>
            <Button onClick={openNew} className="h-8 bg-orange-700 hover:bg-orange-800" data-testid="btn-new-row">
              <Plus className="w-4 h-4 mr-1" /> Add property
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="hp-kpis">
          <Kpi label="Properties" value={totals.total_rows} tone="gray" testid="kpi-count" isCount />
          <Kpi label={`Aggregate Income (${regime})`} value={totals.aggregate_income} tone={totals.aggregate_income >= 0 ? "emerald" : "rose"} testid="kpi-aggregate" />
          <Kpi label="Included in ITR" value={totals.included_income} tone="blue" testid="kpi-included" />
          <Kpi label="Regime Restrictions" value={regime === "New" ? "SOP int NIL · LOP loss clipped" : "None"} tone="amber" testid="kpi-regime" isText />
        </div>

        <div className="border border-orange-200 bg-orange-50 rounded p-3 text-xs text-orange-900 flex gap-2" data-testid="rule-hint">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Regime rules · Sec 115BAC(2)</strong>: Under the New regime, interest on Self-Occupied property is <strong>NOT allowed</strong> (income = 0). For Let-Out, interest is allowed but any resulting loss cannot be set off against other heads (only carried forward for 8 years). Change the regime selector above to preview the impact.
          </div>
        </div>

        {/* Rows */}
        <div className="border border-gray-200 bg-white rounded overflow-hidden" data-testid="rows-table">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-700 flex items-center justify-between">
            <span>Properties — AY {ay}</span>
            {loading && <span className="text-gray-400">Loading…</span>}
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
              <Home className="w-10 h-10 text-gray-300" />
              No properties yet for AY {ay}.
            </div>
          ) : (
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Type</th>
                  <th className="text-right">GAV</th>
                  <th className="text-right">NAV</th>
                  <th className="text-right">30% Ded</th>
                  <th className="text-right">Interest allowed</th>
                  <th className="text-right">Income / (Loss)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const meta = perRow.find((p) => p.id === r.id) || {};
                  const isNeg = (meta.income_from_hp ?? 0) < 0;
                  return (
                    <tr key={r.id} data-testid={`row-${i}`}>
                      <td className="text-xs">
                        <div className="font-medium">{r.property_name || "—"}</div>
                        {r.address && <div className="text-[10px] text-gray-500">{r.address}</div>}
                        {meta.warnings?.length > 0 && (
                          <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-700">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                            <div>{meta.warnings[0]}</div>
                          </div>
                        )}
                      </td>
                      <td className="text-xs">{TYPE_LABEL[r.property_type]}</td>
                      <td className="tx-num">{formatINR(meta.gav || 0)}</td>
                      <td className="tx-num">{formatINR(meta.nav || 0)}</td>
                      <td className="tx-num">{formatINR(meta.std_ded_30 || 0)}</td>
                      <td className="tx-num">{formatINR(meta.interest_allowed || 0)}</td>
                      <td className={`tx-num font-medium ${isNeg ? "text-rose-700" : ""}`}>
                        {formatINR(meta.income_from_hp || 0)}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        <button onClick={() => openEdit(r)} className="text-blue-700 hover:underline mr-2" title="Edit" data-testid={`btn-edit-${i}`}>
                          <Pencil className="w-3.5 h-3.5 inline" />
                        </button>
                        <button onClick={() => del(r)} className="text-red-600 hover:text-red-800" title="Delete" data-testid={`btn-delete-${i}`}>
                          <Trash2 className="w-3.5 h-3.5 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="row-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Add"} Property</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Property name" value={editing.property_name} onChange={(v) => setEditing({ ...editing, property_name: v })} testid="input-property-name" />
                <FieldSelect label="Property type" value={editing.property_type} onChange={(v) => setEditing({ ...editing, property_type: v })} testid="select-property-type">
                  {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </FieldSelect>
                <Field label="Address (optional)" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} />
              </div>

              {isLop && (
                <SubSection title="Rental — used to compute GAV">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <FieldNum label="Actual rent received" value={editing.rent_received} onChange={(v) => setEditing({ ...editing, rent_received: v })} testid="input-rent" />
                    <FieldNum label="Municipal value" value={editing.municipal_value} onChange={(v) => setEditing({ ...editing, municipal_value: v })} testid="input-muni-val" />
                    <FieldNum label="Fair rent" value={editing.fair_rent} onChange={(v) => setEditing({ ...editing, fair_rent: v })} testid="input-fair-rent" />
                    <FieldNum label="Standard rent (Rent Control — optional)" value={editing.standard_rent} onChange={(v) => setEditing({ ...editing, standard_rent: v })} testid="input-std-rent" />
                    <FieldNum label="Vacancy period (months)" value={editing.vacancy_period_months} onChange={(v) => setEditing({ ...editing, vacancy_period_months: v })} />
                    <FieldNum label="Unrealised rent (Sec 25AA)" value={editing.unrealised_rent} onChange={(v) => setEditing({ ...editing, unrealised_rent: v })} />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    GAV = max(actual rent, expected rent). Expected rent = min(standard rent, max(municipal value, fair rent)).
                  </p>
                </SubSection>
              )}

              <SubSection title="Municipal taxes & interest deductions">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {isLop && (
                    <FieldNum label="Municipal taxes paid (by owner)" value={editing.municipal_taxes_paid} onChange={(v) => setEditing({ ...editing, municipal_taxes_paid: v })} testid="input-muni-tax" />
                  )}
                  <FieldNum label="Interest on housing loan — current year" value={editing.interest_current_year} onChange={(v) => setEditing({ ...editing, interest_current_year: v })} testid="input-interest" />
                  <FieldNum label="Pre-construction interest TOTAL" value={editing.pre_construction_interest_total} onChange={(v) => setEditing({ ...editing, pre_construction_interest_total: v })} testid="input-pre-total" />
                  <FieldNum label="Years remaining (of 5-year amortisation)" value={editing.pre_construction_years_remaining} onChange={(v) => setEditing({ ...editing, pre_construction_years_remaining: v })} />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Pre-construction interest is spread over 5 years (1/5 pa) starting from the AY of completion. Engine allocates ₹{formatINR((editing.pre_construction_interest_total || 0) / 5)} this year.
                </p>
                {editing.property_type === "SOP" && (
                  <label className="flex items-center gap-2 text-xs mt-2">
                    <input type="checkbox" checked={editing.loan_taken_before_1_apr_1999} onChange={(e) => setEditing({ ...editing, loan_taken_before_1_apr_1999: e.target.checked })} data-testid="check-loan-pre-1999" />
                    Loan taken before 1-Apr-1999 (interest cap = ₹30,000)
                  </label>
                )}
              </SubSection>

              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={editing.include_in_computation} onChange={(e) => setEditing({ ...editing, include_in_computation: e.target.checked })} data-testid="check-include" />
                Include in ITR push
              </label>
              <Field label="Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="btn-cancel"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} className="bg-orange-700 hover:bg-orange-800" data-testid="btn-save"><Check className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent data-testid="push-dialog">
          <DialogHeader>
            <DialogTitle>Push house property to ITR</DialogTitle>
          </DialogHeader>
          {computations.length === 0 ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              No saved computations found. <a href="/tax/computation/new" className="underline">Create one first</a> for AY {ay}.
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs">Choose the target computation</Label>
              <select value={selectedCompId} onChange={(e) => setSelectedCompId(e.target.value)} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="select-target-comp">
                {computations.map((c) => (
                  <option key={c.id} value={c.id}>{c.ay} — {c.profile_snapshot?.name || c.profile_snapshot?.taxpayer_type} ({c.profile_snapshot?.tax_regime})</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500">
                Push uses the target computation&apos;s regime (not the preview toggle). SOP interest is zeroed and LOP losses clipped under New; unrestricted under Old.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushOpen(false)}>Cancel</Button>
            <Button onClick={pushToITR} disabled={!selectedCompId} className="bg-orange-700 hover:bg-orange-800" data-testid="btn-confirm-push">
              <Send className="w-4 h-4 mr-1" /> Push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* helpers */
function Kpi({ label, value, tone, testid, isText = false, isCount = false }) {
  const bg = { gray: "bg-gray-50", emerald: "bg-emerald-50", rose: "bg-rose-50", blue: "bg-blue-50", amber: "bg-amber-50" }[tone];
  const fg = { gray: "text-gray-900", emerald: "text-emerald-900", rose: "text-rose-900", blue: "text-blue-900", amber: "text-amber-900" }[tone];
  return (
    <div className={`border border-gray-200 ${bg} rounded p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-600">{label}</div>
      <div className={`${isText ? "text-xs" : "text-lg"} font-medium ${fg}`}>
        {isText ? value : (isCount ? value : `₹ ${formatINR(value)}`)}
      </div>
    </div>
  );
}
function SubSection({ title, children }) {
  return (
    <div className="border border-gray-200 rounded p-3 bg-gray-50/40">
      <div className="text-[11px] uppercase tracking-wider text-gray-600 mb-2">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 text-xs" data-testid={testid} />
    </div>
  );
}
function FieldNum({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input type="number" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-9 text-xs" data-testid={testid} />
    </div>
  );
}
function FieldSelect({ label, value, onChange, children, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid={testid}>
        {children}
      </select>
    </div>
  );
}

export default function HouseProperty() {
  return <RequireCompany><Inner /></RequireCompany>;
}
